/**
 * Create Organization Modal (Phase 30 Wave 4 chunk 30.4.3b)
 *
 * Super-admin-only modal for creating a new organization. Hosted by
 * `OrganizationsPage`. On success, the modal closes and the page's
 * organizations query auto-refetches via TanStack Query invalidation
 * (see `useCreateOrganization` in `hooks/useOrganizations.ts`).
 *
 * Pattern reference: `components/sops/SOPUploadModal.tsx`
 *  - Backdrop (click-to-close) + centered card
 *  - Escape closes the modal (when not submitting)
 *  - `bg-white` on every form input (per `.claude/rules/critical-patterns.md`)
 *  - `disabled` on inputs + buttons during submission
 *  - Inline red-banner error display
 *
 * Validation:
 *  - name: required, 1-255 chars
 *  - slug: required, kebab-case `^[a-z0-9]+(-[a-z0-9]+)*$`, 1-50 chars
 *  - auth0OrgId: optional, format `org_xxxxxxxxxxxxxxxx`
 *
 * 409 conflict (slug already exists) is surfaced as a field-level error on
 * the slug input. Other failures render in the inline-banner.
 */

import { useEffect, useRef, useState } from 'react';
import { Building2, Loader2, Plus, X } from 'lucide-react';
import { useCreateOrganization } from '../../../hooks/useOrganizations';
import { ApiError } from '../../../lib/api';

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (orgId: string) => void;
}

interface FormData {
  name: string;
  slug: string;
  auth0OrgId: string;
}

const initialFormData: FormData = {
  name: '',
  slug: '',
  auth0OrgId: '',
};

const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const AUTH0_ORG_ID_REGEX = /^org_[A-Za-z0-9]+$/;

export function CreateOrganizationModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateOrganizationModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [slugFieldError, setSlugFieldError] = useState<string | null>(null);
  const [auth0OrgIdFieldError, setAuth0OrgIdFieldError] = useState<string | null>(null);

  const firstInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateOrganization();
  const isSubmitting = createMutation.isPending;

  // Reset state shortly after the modal closes so the next open has a
  // clean form (mirrors `SOPUploadModal.tsx` pattern).
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setFormData(initialFormData);
        setSubmitError(null);
        setSlugFieldError(null);
        setAuth0OrgIdFieldError(null);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Focus the first input on open.
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen]);

  // Escape closes the modal (only when not submitting).
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  const validate = (): boolean => {
    let ok = true;
    setSlugFieldError(null);
    setAuth0OrgIdFieldError(null);
    setSubmitError(null);

    const name = formData.name.trim();
    const slug = formData.slug.trim();
    const auth0OrgId = formData.auth0OrgId.trim();

    if (!name) {
      setSubmitError('Name is required');
      ok = false;
    } else if (name.length > 255) {
      setSubmitError('Name must be 255 characters or fewer');
      ok = false;
    }

    if (!slug) {
      setSlugFieldError('Slug is required');
      ok = false;
    } else if (slug.length > 50) {
      setSlugFieldError('Slug must be 50 characters or fewer');
      ok = false;
    } else if (!SLUG_REGEX.test(slug)) {
      setSlugFieldError(
        'Slug must be lowercase alphanumeric with single dashes (e.g., acme-manufacturing)'
      );
      ok = false;
    }

    if (auth0OrgId && !AUTH0_ORG_ID_REGEX.test(auth0OrgId)) {
      setAuth0OrgIdFieldError('Auth0 Org ID must look like org_xxxxxxxxxxxxxxxx');
      ok = false;
    }

    return ok;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    try {
      const created = await createMutation.mutateAsync({
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        auth0OrgId: formData.auth0OrgId.trim() || null,
      });
      onSuccess?.(created.id);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Field-level surfacing for slug-conflict (the only 23505 path
        // exposed by the backend create handler — see orgs.ts:194-205).
        setSlugFieldError(err.message);
      } else if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Failed to create organization');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-organization-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={!isSubmitting ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 flex items-center justify-center shadow-lg shadow-guild-green/20">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2
                id="create-organization-modal-title"
                className="text-xl font-bold text-gray-900 tracking-tight"
              >
                Create Organization
              </h2>
              <p className="text-sm text-gray-500">
                Add a new customer tenant to the platform
              </p>
            </div>
          </div>

          {!isSubmitting && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-6 right-6 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-8 py-6">
            {/* Submit-level error banner */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <X className="w-3 h-3 text-red-500" />
                </div>
                <p className="text-red-700 text-sm">{submitError}</p>
              </div>
            )}

            <div className="space-y-5">
              {/* Name */}
              <div>
                <label
                  htmlFor="create-org-name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-org-name"
                  ref={firstInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  disabled={isSubmitting}
                  placeholder="e.g., Acme Manufacturing"
                  maxLength={255}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green outline-none transition-all text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500 bg-white"
                />
              </div>

              {/* Slug */}
              <div>
                <label
                  htmlFor="create-org-slug"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Slug <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-org-slug"
                  type="text"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      // Normalize slug input on the fly: trim + lowercase
                      // so users can't accidentally submit `Acme-Manufacturing`.
                      // Spaces are turned into hyphens for ergonomics; the
                      // regex still rejects double hyphens etc.
                      slug: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                    }))
                  }
                  onBlur={(e) =>
                    setFormData((prev) => ({ ...prev, slug: e.target.value.trim() }))
                  }
                  disabled={isSubmitting}
                  placeholder="e.g., acme-manufacturing"
                  maxLength={50}
                  aria-invalid={slugFieldError ? true : undefined}
                  aria-describedby={
                    slugFieldError
                      ? 'create-org-slug-error'
                      : 'create-org-slug-help'
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 outline-none transition-all text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500 bg-white ${
                    slugFieldError
                      ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                      : 'border-gray-200 focus:ring-guild-green/20 focus:border-guild-green'
                  }`}
                />
                {slugFieldError ? (
                  <p
                    id="create-org-slug-error"
                    className="mt-1.5 text-xs text-red-600"
                  >
                    {slugFieldError}
                  </p>
                ) : (
                  <p
                    id="create-org-slug-help"
                    className="mt-1.5 text-xs text-gray-500"
                  >
                    Lowercase alphanumeric and dashes only. Used in URLs.
                  </p>
                )}
              </div>

              {/* Auth0 Org ID (optional) */}
              <div>
                <label
                  htmlFor="create-org-auth0-id"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Auth0 Organization ID
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    (optional)
                  </span>
                </label>
                <input
                  id="create-org-auth0-id"
                  type="text"
                  value={formData.auth0OrgId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      auth0OrgId: e.target.value,
                    }))
                  }
                  disabled={isSubmitting}
                  placeholder="org_xxxxxxxxxxxxxxxx"
                  aria-invalid={auth0OrgIdFieldError ? true : undefined}
                  aria-describedby={
                    auth0OrgIdFieldError
                      ? 'create-org-auth0-id-error'
                      : 'create-org-auth0-id-help'
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 outline-none transition-all text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500 bg-white ${
                    auth0OrgIdFieldError
                      ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                      : 'border-gray-200 focus:ring-guild-green/20 focus:border-guild-green'
                  }`}
                />
                {auth0OrgIdFieldError ? (
                  <p
                    id="create-org-auth0-id-error"
                    className="mt-1.5 text-xs text-red-600"
                  >
                    {auth0OrgIdFieldError}
                  </p>
                ) : (
                  <p
                    id="create-org-auth0-id-help"
                    className="mt-1.5 text-xs text-gray-500"
                  >
                    Leave blank if not using Auth0 Organizations.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-5 py-2.5 text-gray-600 font-semibold hover:text-gray-900 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 bg-guild-green text-white font-semibold rounded-xl hover:bg-guild-green/95 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-guild-green/20"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Organization
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateOrganizationModal;
